import React, { useEffect, useRef } from "react";
import Editor, { EditorProps } from "@monaco-editor/react";
import { Box } from "@mui/material";
import * as monaco from "monaco-editor";

export interface JsonEditorProps extends Omit<EditorProps, "onMount"> {
  onFocus?: () => void;
  onBlur?: () => void;
  hasError?: boolean;
  className?: string;
  testId?: string;
  disabled?: boolean;
}

const JsonEditor: React.FC<JsonEditorProps> = ({
  onFocus,
  onBlur,
  hasError = false,
  className,
  testId,
  disabled = false,
  height = "200px",
  defaultLanguage = "json",
  options,
  ...editorProps
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  useEffect(() => {
    monaco.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [],
      schemaValidation: "error",
      schemaRequest: "warning",
    });
  }, []);

  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;

    // Format the document on load
    editor.getAction("editor.action.formatDocument")?.run();

    // Handle focus events
    if (onFocus) {
      editor.onDidFocusEditorText(() => {
        onFocus();
      });
    }

    // Handle blur events
    if (onBlur) {
      editor.onDidBlurEditorText(() => {
        onBlur();
      });
    }
  };

  return (
    <Box border={1} borderColor={hasError ? "error.main" : "grey.400"} borderRadius={1} className={className} data-testid={testId} p={1}>
      <Editor
        height={height}
        defaultLanguage={defaultLanguage}
        onMount={handleEditorDidMount}
        options={{
          minimap: { enabled: false },
          formatOnPaste: true,
          readOnly: disabled,
          ...options,
        }}
        {...editorProps}
      />
    </Box>
  );
};

export default JsonEditor;
